import { computed, ref } from 'vue'
import {
  ECOMMERCE_ITEM_BENEFIT_TYPE,
  ECOMMERCE_ITEM_PRICE_TYPE,
  ECOMMERCE_ITEM_TAG,
  type EcommerceItem,
  type EcommerceItemPrice,
  SALE_DISCOUNT_TYPE,
  SPECIAL_ECOMMERCE_ITEM_ID,
} from '@/types/shared/player-config'
import api from '@/services/api'
import { useAnalytics } from '@/composables/analytics'
import { sleep } from '@/services/helper'
import { useConfig } from '@/composables/game/config'
import { useWalletConnect } from '@/composables/game/wallet-connect'
import { player, syncPlayer } from '@/composables/game/player'
import { watchOnce } from '@vueuse/core'
import { useTonConnectModal } from '@townsquarelabs/ui-vue'
import { hideTonPayment } from '@/composables/init'
import webapp from '@/services/webapp'
import { useHapticFeedback } from '@/composables/game/haptic-feedback'
import { Address } from '@ton/core'
import { env } from '@/env'
import { isBaseMiniApp } from '@/services/base'
import { pay as basePay } from '@base-org/account'
import { usePlayerSegmenter } from '@/composables/game/player-segmenter'

export type PurchaseMetadata = {
  type: 'puzzle'
  step: number
} | {
  type: 'exchange'
  itemId: string
} | undefined

type PurchaseInput = ({
  type: 'item'
  item: EcommerceItem
  priceType?: ECOMMERCE_ITEM_PRICE_TYPE
} | {
  type: 'card',
  upgradeId: string,
  price: EcommerceItemPrice
  priceType?: ECOMMERCE_ITEM_PRICE_TYPE
  itemId: SPECIAL_ECOMMERCE_ITEM_ID.CARD_UPGRADE | SPECIAL_ECOMMERCE_ITEM_ID.CARD_FAST_FORWARD
} | {
  type: 'batch-card-fast-forward',
  item: EcommerceItem
  priceType: ECOMMERCE_ITEM_PRICE_TYPE
}) & { metadata?: PurchaseMetadata | undefined }

type PurchaseOutput = { paid: false } | { paid: true, nonce: string }

const starsBalanceConfirmModalConfig = ref<{ amount: number, accepted: boolean }>({ amount: 0, accepted: false })
const showStarsBalanceConfirmModal = computed(() => starsBalanceConfirmModalConfig.value.amount > 0)
const buyingItemId = ref<null | string>(null)

export const jettonMasters = {
  dev: {
    [ECOMMERCE_ITEM_PRICE_TYPE.USDT]: Address.parse('EQCxE6mUtQJKFnGfaROTKOt1lZbDiiX1kCixRv7Nw2Id_sDs'),
    [ECOMMERCE_ITEM_PRICE_TYPE.DROPEE]: Address.parse('EQCKRayqq5dvYlB1RjtIkSIw0_BS7jX6FaJsZH2DpOzQ2BWM'),
  },
  prod: {
    [ECOMMERCE_ITEM_PRICE_TYPE.USDT]: Address.parse('EQCxE6mUtQJKFnGfaROTKOt1lZbDiiX1kCixRv7Nw2Id_sDs'),
    [ECOMMERCE_ITEM_PRICE_TYPE.DROPEE]: Address.parse('EQDPGjm4PU81Dez2rmQFQU2hw_i8hut5GTU91HOtzZbPEWf8'),
  },
} as const

export const useEcommerceItems = () => {
  const { config } = useConfig()
  const { segment } = usePlayerSegmenter()
  return {
    ecommerceItems: computed(() => config.value.game.ecommerce.items.filter(item => segment(item.segmentation) && !item.archivedAt)),
  }
}

export const useEcommerce = () => {
  const { triggerEvent } = useAnalytics()
  const { config } = useConfig()
  const { getStarPrice } = useEcommercePrice()
  const { doTonPurchaseTransaction, doJettonPurchaseTransaction } = useWalletConnect()

  const playerStarsBalance = computed(() => player.value.ecommerce.starsBalance)

  function openInvoice(invoiceUrl: string) {
    return webapp.openInvoice(invoiceUrl)
  }

  function buildItemAnalyticsMetadata(item: EcommerceItem, usdAmount?: number) {
    return {
      item_id: item.id,
      item_name: item.name,
      index: 0,
      price: usdAmount ?? getStarPrice(item)?.usdAmount ?? 0,
      quantity: 1,
    }
  }

  async function purchaseAndGetNonce(input: PurchaseInput): Promise<PurchaseOutput> {
    const item = 'item' in input ? input.item : ('itemId' in input ? config.value.game.ecommerce.items.find(i => i.id === input.itemId) : undefined)
    if (!item) {
      await api.reportError({
        code: 'upgrade_card_not_found',
        metadata: {},
        description: 'cardUpgrade item not found',
      })
      throw Error('cardUpgrade item not found')
    }

    const itemMetadata = buildItemAnalyticsMetadata(item, input.type === 'card' ? input.price.usdAmount : item.prices[0]?.usdAmount)
    const itemPriceType = input.type === 'batch-card-fast-forward' ? input.priceType
      : (input.type === 'item' ?
        (('priceType' in input) ? (input.priceType ?? ECOMMERCE_ITEM_PRICE_TYPE.STAR) : ECOMMERCE_ITEM_PRICE_TYPE.STAR) :
        input.price.type)
    const isStarsPayment = itemPriceType === ECOMMERCE_ITEM_PRICE_TYPE.STAR

    const ecommerceMetadata = {
      currency: 'USD',
      value: itemMetadata.price,
      items: [
        itemMetadata,
      ],
    }
    triggerEvent({
      event: 'begin_checkout',
      metadata: {
        ecommerce: ecommerceMetadata,
      },
    })
    buyingItemId.value = item.id
    try {
      let invoiceUrl = ''
      let nonce = ''
      let response: {
        isPaid: false
        invoiceUrl: string
        nonce: string
        amount: number
      } | {
        isPaid: true
        nonce: string
      } | null = null
      if (input.type === 'item') {
        response = await api.getEcommerceInvoiceUrl({
          itemId: item.id,
          priceType: itemPriceType,
          metadata: input.metadata,
        })
      } else if (input.type === 'card') {
        response = await api.getCardInvoiceUrl({
          upgradeId: input.upgradeId,
          itemId: input.itemId,
          priceType: itemPriceType,
        })
      } else {
        response = await api.getBatchCardFastForwardInvoiceUrl({
          priceType: itemPriceType,
        })
      }
      if (response) {
        if (response.isPaid) {
          return { paid: true, nonce: response.nonce }
        }
        invoiceUrl = response.invoiceUrl
        nonce = response.nonce
        if (isBaseMiniApp && itemPriceType === ECOMMERCE_ITEM_PRICE_TYPE.USDT) {
          const payment = await basePay({
            amount: response.amount.toString(),
            to: '0xB76891E3c7005A9DaCE8c180C0064819F20f1A72',
          })
          if (!payment) {
            throw new Error('payment not done')
          }
          await api.resolveInvoiceByTx({
            nonce: nonce,
            txHash: payment.id,
          })
        } else if (itemPriceType === ECOMMERCE_ITEM_PRICE_TYPE.TON) {
          await doTonPurchaseTransaction({
            tonAmount: response.amount,
            nonce: nonce,
          })
        } else if ([ECOMMERCE_ITEM_PRICE_TYPE.USDT, ECOMMERCE_ITEM_PRICE_TYPE.DROPEE].includes(itemPriceType)) {
          await doJettonPurchaseTransaction({
            address: jettonMasters[env.app.isProd ? 'prod' : 'dev'][itemPriceType as ECOMMERCE_ITEM_PRICE_TYPE.USDT | ECOMMERCE_ITEM_PRICE_TYPE.DROPEE],
            amount: response.amount,
            nonce: nonce,
          })
        }
      }

      let result = ''
      if (isStarsPayment) {
        console.log('opening invoice')
        result = await openInvoice(invoiceUrl) as string
        console.log('got invoice result', result)
        if (result !== 'paid') {
          return { paid: false }
        }
      }
      for (let i = 0; i < (isStarsPayment ? 15 : 60); i++) {
        await sleep(isStarsPayment ? 1000 : 3000)
        const { status } = await api.getPurchaseStatus({ nonce })
        if (status === 'completed') {
          result = 'paid'
          break
        }
      }

      if (result === '') {
        throw new Error('purchase timed out')
      }

      if (result === 'paid' && itemPriceType !== ECOMMERCE_ITEM_PRICE_TYPE.TON) {
        triggerEvent({
          event: 'purchase',
          metadata: {
            value: itemMetadata.price,
            product_item_id: item.trackingId ?? item.id,
            product_item_name: item.name,
            discountType: config.value.game.ecommerce.sales?.discount?.type ?? undefined,
            discountAmount: (config.value.game.ecommerce.sales?.discount as {
              type: SALE_DISCOUNT_TYPE.DISCOUNT | SALE_DISCOUNT_TYPE.CASHBACK,
              amount: number
            })?.amount ?? undefined,
            ecommerce: {
              ...ecommerceMetadata,
              transaction_id: nonce,
            },
          },
        })
      }

      return { paid: result === 'paid', nonce }

    } catch (e) {
      console.log('Purchase error:', e)
      await api.reportError({
        code: 'failedToPurchase',
        description: (e as Error).message,
        metadata: item,
      })
      return { paid: false }
    } finally {
      buyingItemId.value = null
    }
  }

  async function confirmAndPurchase(input: PurchaseInput): Promise<PurchaseOutput> {
    const item = 'item' in input ? input.item : ('itemId' in input ? config.value.game.ecommerce.items.find(i => i.id === input.itemId) : undefined)
    if (!item) {
      await api.reportError({
        code: 'upgrade_card_not_found',
        metadata: {},
        description: 'cardUpgrade item not found',
      })
      throw Error('cardUpgrade item not found')
    }

    // For batch fast-forward, skip confirmation since price is dynamic and calculated on server
    if (input.type === 'batch-card-fast-forward') {
      return await purchaseAndGetNonce(input)
    }

    const price: EcommerceItemPrice | undefined = input.type === 'item'
      ? item.prices.find(p => p.type === (input.priceType ?? ECOMMERCE_ITEM_PRICE_TYPE.STAR))
      : input.price

    if (!price) {
      await api.reportError({
        code: 'price_type_not_found',
        metadata: { input },
        description: 'missing price type',
      })
      throw Error('missing price type')
    }

    const isStarsPayment = input.priceType === ECOMMERCE_ITEM_PRICE_TYPE.STAR
    const hasStarBenefit = item.benefits.some(b => b.type === ECOMMERCE_ITEM_BENEFIT_TYPE.STAR)
    const isFree = price.amount === 0

    if (hasStarBenefit || isFree || !isStarsPayment || playerStarsBalance.value < price.amount || item.config.disableInGameStar) {
      return await purchaseAndGetNonce(input)
    }

    const isConfirmed = await showConfirmModal(price.amount)
    if (!isConfirmed) return { paid: false }

    const result = await purchaseAndGetNonce(input)
    starsBalanceConfirmModalConfig.value = {
      amount: 0,
      accepted: false,
    }
    return result
  }

  function showConfirmModal(amount: number) {
    return new Promise((resolve) => {
      starsBalanceConfirmModalConfig.value = {
        amount,
        accepted: false,
      }

      watchOnce(starsBalanceConfirmModalConfig, (starsBalanceConfirmModalConfig) => {
        resolve(starsBalanceConfirmModalConfig.accepted)
      }, { deep: true })
    })
  }

  return {
    buyingItemId: buyingItemId,
    purchase: confirmAndPurchase,
    buildItemAnalyticsMetadata: buildItemAnalyticsMetadata,
    showStarsBalanceConfirmModal,
    starsBalanceConfirmModalConfig,
    showConfirmModal,
  }
}

export const useLimitedTimeOffers = () => {
  const { ecommerceItems } = useEcommerceItems()
  const starterPackConfig = computed(() => ecommerceItems.value.find(i => !i.archivedAt && i.tags.includes(ECOMMERCE_ITEM_TAG.STARTER)))
  const afterStarterPackConfig = computed(() => ecommerceItems.value.find(i => !i.archivedAt && i.tags.includes(ECOMMERCE_ITEM_TAG.AFTER_STARTER)))
  const afterStarterPackDuration = computed(() => afterStarterPackConfig.value?.limits.time?.duration ?? 0)
  return {
    starterPack: starterPackConfig,
    afterStarterPack: afterStarterPackConfig,
    afterStarterPackDuration,
  }
}

export const useEcommercePrice = () => {

  const getStarPrice = (item: EcommerceItem) => item.prices.find(p => p.type === ECOMMERCE_ITEM_PRICE_TYPE.STAR) as EcommerceItemPrice
  const getTonPrice = (item: EcommerceItem) => item.prices.find(p => p.type === ECOMMERCE_ITEM_PRICE_TYPE.TON) ?? null
  const getDropeePrice = (item: EcommerceItem) => item.prices.find(p => p.type === ECOMMERCE_ITEM_PRICE_TYPE.DROPEE) ?? null
  const getUsdtPrice = (item: EcommerceItem) => item.prices.find(p => p.type === ECOMMERCE_ITEM_PRICE_TYPE.USDT) ?? null

  return {
    getStarPrice,
    getTonPrice,
    getDropeePrice,
    getUsdtPrice,
  }
}

export const useEcommerceItemDetailsPurchase = (item: EcommerceItem) => {
  const { getStarPrice, getTonPrice, getDropeePrice, getUsdtPrice } = useEcommercePrice()
  const { purchase, buyingItemId } = useEcommerce()
  const { rawAddress } = useWalletConnect()
  const { open } = useTonConnectModal()
  const { syncConfig } = useConfig()
  const { addHapticFeedback } = useHapticFeedback()

  const starPrice = computed(() => getStarPrice(item))
  const tonPrice = computed(() => getTonPrice(item))
  const dropeePrice = computed(() => getDropeePrice(item))
  const usdtPrice = computed(() => getUsdtPrice(item))
  const selectedPriceType = ref<ECOMMERCE_ITEM_PRICE_TYPE | null>(null)

  const isBuying = computed(() => buyingItemId.value === item.id)
  const isBuyingWithStars = computed(() => isBuying.value && selectedPriceType.value === ECOMMERCE_ITEM_PRICE_TYPE.STAR)
  const isBuyingWithTon = computed(() => isBuying.value && selectedPriceType.value === ECOMMERCE_ITEM_PRICE_TYPE.TON)
  const isBuyingWithDropee = computed(() => isBuying.value && selectedPriceType.value === ECOMMERCE_ITEM_PRICE_TYPE.DROPEE)
  const isBuyingWithUsdt = computed(() => isBuying.value && selectedPriceType.value === ECOMMERCE_ITEM_PRICE_TYPE.USDT)
  const isBuyingWithGoldenCoins = computed(() => isBuying.value && selectedPriceType.value === ECOMMERCE_ITEM_PRICE_TYPE.GOLDEN_COIN)

  const showTonPurchase = computed(() => !hideTonPayment.value && !!tonPrice.value && tonPrice.value.amount > 0)

  const canHaveSale = computed(() => item.tags.includes(ECOMMERCE_ITEM_TAG.CAN_HAVE_DISCOUNT))

  async function onPurchase(priceType: ECOMMERCE_ITEM_PRICE_TYPE, metadata?: PurchaseMetadata): Promise<{ paid: boolean, nonce?: string }> {
    addHapticFeedback('light')
    if (isBuying.value) return { paid: false }
    selectedPriceType.value = priceType

    try {
      if (!isBaseMiniApp && [ECOMMERCE_ITEM_PRICE_TYPE.TON, ECOMMERCE_ITEM_PRICE_TYPE.USDT, ECOMMERCE_ITEM_PRICE_TYPE.DROPEE].includes(priceType) && !rawAddress.value) {
        open()

        // Wrap the watchOnce in a Promise with timeout to prevent indefinite hangs
        const waitForAddress = new Promise<{ paid: boolean, nonce?: string }>((resolve) => {

          const timeoutId = setTimeout(() => {
            selectedPriceType.value = null
            resolve({ paid: false })
          }, 30000)

          watchOnce(rawAddress, async () => {
            try {
              if (!rawAddress.value) {
                resolve({ paid: false })
                return
              }

              const result = await doPurchase(priceType, metadata)
              resolve(result)
            } catch (e) {
              console.error('waitForAddress doPurchase failed:', e)
              resolve({ paid: false })
            } finally {
              clearTimeout(timeoutId)
              selectedPriceType.value = null
            }
          })
        })

        return await waitForAddress
      }

      const result = await doPurchase(priceType, metadata)
      selectedPriceType.value = null
      return result
    } catch (e) {
      console.error('onPurchase failed:', e)
      selectedPriceType.value = null
      return { paid: false }
    }
  }

  async function doPurchase(priceType: ECOMMERCE_ITEM_PRICE_TYPE, metadata?: PurchaseMetadata): Promise<{ paid: boolean, nonce?: string }> {
    const result = await purchase({
      type: item.id === SPECIAL_ECOMMERCE_ITEM_ID.CARD_FAST_FORWARD_ALL ? 'batch-card-fast-forward' : 'item',
      item: item,
      priceType: priceType,
      metadata: metadata,
    })
    if (result.paid) {
      addHapticFeedback('medium')
      await syncPlayer()
      await syncConfig()
      return { paid: true, nonce: result.nonce }
    }
    return { paid: false }
  }

  return {
    starPrice,
    tonPrice,
    selectedPriceType,
    isBuying,
    isBuyingWithStars,
    isBuyingWithTon,
    isBuyingWithDropee,
    isBuyingWithUsdt,
    isBuyingWithGoldenCoins,
    dropeePrice,
    usdtPrice,
    showTonPurchase,
    canHaveSale,
    onPurchase,
  }
}
